import { conferenceLabel } from '../lib/text.js'
import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import Avatar from '../components/Avatar.jsx'
import EventVenueMap from '../components/EventVenueMap.jsx'
import EventIllustration from '../components/EventIllustration.jsx'
import ErrorAlert from '../components/ErrorAlert.jsx'
import EventActions from '../components/EventActions.jsx'
import useMe from '../hooks/useMe.js'
import { formatLocalDateTime, formatVenueTime } from '../lib/datetime.js'
import { PAGE_STANDARD } from '../lib/layout.js'
import { speakerHref, placeHref, tagHref, yearHref } from '../lib/permalinks.js'
import TagPill from '../components/TagPill.jsx'

const BASE = '/api/v1'

async function fetchEvent(id) {
  const res = await fetch(`${BASE}/events/${id}`, { headers: { Accept: 'application/json' } })
  if (res.status === 404) return null
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return res.json()
}

function hasCoords(e) {
  return typeof e?.lat === 'number' && typeof e?.lng === 'number'
}

function addressLines(e) {
  // Each populated field on its own line for the textual address card. We
  // keep the order venue → street → city, region, country so it reads like a
  // physical address rather than a slug list.
  return [
    e.venue,
    e.address,
    [e.city, e.region].filter(Boolean).join(', '),
    e.country,
  ].filter(Boolean)
}

export default function EventDetailPage() {
  const { id } = useParams()
  const [event, setEvent] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const { me } = useMe()

  useEffect(() => {
    setLoading(true)
    setError(null)
    fetchEvent(id)
      .then((e) => setEvent(e))
      .catch((err) => setError(err.message || String(err)))
      .finally(() => setLoading(false))
  }, [id])

  if (loading) {
    return (
      <main className={`${PAGE_STANDARD} pb-12 pt-8 sm:pt-10`}>
        <div className="rounded-2xl bg-white p-8 text-center text-slate-500 shadow-sm ring-1 ring-slate-200/60">
          Loading…
        </div>
      </main>
    )
  }
  if (error) {
    return (
      <main className={`${PAGE_STANDARD} pb-12 pt-8 sm:pt-10`}>
        <ErrorAlert title="Couldn't load event" message={error} source="EventDetailPage" />
      </main>
    )
  }
  if (!event) {
    return (
      <main className={`${PAGE_STANDARD} pb-12 pt-8 sm:pt-10`}>
        <div className="rounded-2xl bg-white p-10 text-center shadow-sm ring-1 ring-slate-200/60">
          <h1 className="text-xl font-semibold text-slate-900">Event not found</h1>
          <p className="mt-2 text-sm text-slate-500">It may have been unpublished or removed.</p>
          <Link to="/" className="mt-4 inline-block rounded-full px-4 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50">
            ← Back to calendar
          </Link>
        </div>
      </main>
    )
  }

  const main = (event.speakers || []).find((s) => s.isMain)
  const co = (event.speakers || []).filter((s) => !s.isMain)
  const lines = addressLines(event)
  // #338: permalinks. A detail page has no year in its path, so these land on
  // the default upcoming view narrowed by whatever was clicked.
  const place = placeHref(event)
  const eventYear = event.startsAtUtc ? new Date(event.startsAtUtc).getUTCFullYear() : null

  return (
    <main className={`${PAGE_STANDARD} pb-12 pt-8 sm:pt-10`}>
      <Link to="/" className="text-sm text-slate-500 hover:text-brand-700">← Back to calendar</Link>

      <header className="mt-3">
        <div className="flex flex-wrap items-center gap-2">
          {event.format ? (
            <span className="inline-flex items-center rounded-full bg-brand-100 px-2.5 py-0.5 text-xs font-medium text-brand-800">
              {event.format.toLowerCase().replace('_', '-')}
            </span>
          ) : null}
          {/* #338: same violet pills as the cards, and each one a permalink. */}
          {event.tags?.map((t) => (
            <TagPill key={t.slug} tag={t} href={tagHref(t)} />
          ))}
          {eventYear ? (
            <Link
              to={yearHref(event.startsAtUtc)}
              className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700 transition hover:bg-slate-200"
            >
              {eventYear}
            </Link>
          ) : null}
        </div>
        <h1 className="mt-2 text-3xl font-bold leading-tight tracking-tight text-slate-900 sm:text-4xl">
          {event.title}
        </h1>
        {conferenceLabel(event) ? (
          <div className="mt-1 text-base text-slate-500">{conferenceLabel(event)}</div>
        ) : null}

        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-slate-600">
          {main ? (
            <span className="inline-flex items-center gap-2">
              <Avatar
                gravatarHash={main.gravatarHash}
                email={main.email}
                displayName={main.displayName}
                size="h-9 w-9"
                textSize="text-sm"
              />
              <Link
                to={speakerHref(main.userId) || '/'}
                className="text-base font-semibold text-slate-900 hover:text-brand-700 hover:underline"
              >
                {main.displayName || `User #${main.userId}`}
              </Link>
            </span>
          ) : null}
          {co.length ? (
            <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
              <span>with</span>
              {co.map((s) => (
                <span key={s.userId} className="inline-flex items-center gap-1">
                  <Avatar
                    gravatarHash={s.gravatarHash}
                    email={s.email}
                    displayName={s.displayName}
                    size="h-6 w-6"
                    textSize="text-[10px]"
                  />
                  <Link to={speakerHref(s.userId) || '/'} className="hover:text-brand-700 hover:underline">
                    {s.displayName || `User #${s.userId}`}
                  </Link>
                </span>
              ))}
            </span>
          ) : null}
        </div>

        <dl className="mt-4 grid grid-cols-1 gap-y-1 text-sm sm:grid-cols-2">
          <div>
            <dt className="inline text-slate-500">Your local: </dt>
            <dd className="inline font-medium text-slate-700">{formatLocalDateTime(event.startsAtUtc)}</dd>
          </div>
          {event.tzId ? (
            <div>
              <dt className="inline text-slate-500">Venue local: </dt>
              <dd className="inline font-medium text-slate-700">
                {formatVenueTime(event.startsAtUtc, event.tzId)} ({event.tzId})
              </dd>
            </div>
          ) : null}
        </dl>
      </header>

      {/* #390: above the abstract, so the conference's own framing of the talk
          is the first thing after the title. Renders nothing when there is no
          illustration, which is almost every event. */}
      <EventIllustration event={event} />

      {event.abstractText ? (
        <section className="mt-8 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200/60 sm:p-8">
          <h2 className="text-base font-semibold uppercase tracking-wider text-slate-500">About this talk</h2>
          <div className="mt-3 whitespace-pre-line text-base text-slate-800">
            {event.abstractText}
          </div>
        </section>
      ) : null}

      {(hasCoords(event) || lines.length) ? (
        <section className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-[2fr_1fr]">
          {hasCoords(event) ? <EventVenueMap event={event} /> : <div />}
          <aside className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200/60">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500">Where</h3>
            {lines.length ? (
              <address className="mt-2 not-italic text-sm leading-6 text-slate-800">
                {lines.map((line) => (<div key={line}>{line}</div>))}
                {place ? (
                  <Link to={place} className="mt-1 inline-block text-sm text-brand-700 hover:underline">
                    All events in {event.city} →
                  </Link>
                ) : null}
              </address>
            ) : (
              <p className="mt-2 text-sm text-slate-500">Location TBA.</p>
            )}
          </aside>
        </section>
      ) : null}

      <EventActions event={event} me={me} />
    </main>
  )
}
